import React from "react";
import customizeProgramCard from "../assets/img/customize-your-program-card.jpeg";

/* ------------------------------------------------------------------
   IPDI Program Cards — data-driven React component
------------------------------------------------------------------- */

const programs = [
  {
    image:
      "https://images.unsplash.com/photo-1487958449943-2429e8be8625?auto=format&fit=crop&w=800&q=80",
    badge: "100% Placement Assured",
    eyebrow: "Flagship Program",
    title: "Executive Architect Program",
    meta: ["6 Months", "Live + Practical"],
    valueProp:
      "Become an industry-ready architect through live projects, BIM workflows, Architecture Services and placement support.",
    highlights: [
      "Tailored Learning Path",
      "Live Project Enrolment",
      "BIM Architecture + AI Render Tools",
      "100% Placement Aid",
    ],
    audience: ["B.Arch Students", "Fresh Graduates", "Junior Architects"],
    ctaLabel: "View Program",
    link: "/course-executive-architect",
  },
  {
    image:
      "https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?auto=format&fit=crop&w=800&q=80",
    badge: "100% Placement Assistance",
    eyebrow: "Flagship Program",
    title: "Executive Interior Designer",
    meta: ["5 Months", "15 hrs/week"],
    valueProp:
      "We build the path from imagination to execution and open doors to become part of world-renowned organizations.",
    highlights: [
      "National + Intl. Expert Trainers",
      "Live Project Enrolment",
      "250 Hours Live Sessions",
      "100% Placement Assistance",
    ],
    audience: ["B.Des Graduates", "Diploma Holders", "Design Aspirants"],
    ctaLabel: "View Program",
    link: "/course-executive-interior",
  },
  {
    image: customizeProgramCard,
    badge: "100% Placement Assistance",
    eyebrow: "Build Your Own",
    title: "Customize Your Program",
    meta: ["As Per Module"],
    valueProp:
      "Unlock your potential. Upgrade your skills and become deserving of the next position — a training path designed around you.",
    highlights: [
      "Career Counselling Included",
      "Live Projects, Assignment Based",
      "Choose From 15+ Modules",
      "100% Placement Assistance",
    ],
    audience: ["Professionals", "Career Switchers", "B.Arch + 1yr Exp."],
    ctaLabel: "Start Application",
    link: "/customize-your-program",
  },
];

/* ---------------- Icons (inline, no external deps) ---------------- */

const IconClock = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 3" />
  </svg>
);
const IconVideo = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
    <path d="M23 7l-7 5 7 5V7z" />
    <rect x="1" y="5" width="15" height="14" rx="2" />
  </svg>
);
const IconTarget = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
    <circle cx="12" cy="12" r="3" />
    <path d="M12 1v6m0 10v6m11-11h-6M7 12H1" />
  </svg>
);
const IconStar = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
    <path d="M12 2l3 6 6 1-4.5 4 1 6-5.5-3-5.5 3 1-6L3 9l6-1z" />
  </svg>
);
const IconCheck = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);
const IconArrow = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);
const IconBadge = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
    <path d="M12 2l9 4.5v11L12 22l-9-4.5v-11L12 2z" />
  </svg>
);

const highlightIcons = [IconTarget, IconVideo, IconStar, IconCheck];

/* ---------------- Single card ---------------- */

function ProgramCard({ program }) {
  return (
    <div className="ipdi-card">
      <div className="ipdi-card-image">
        <img src={program.image} alt={program.title} loading="lazy" />
        {/* eyebrow badge — top-left, opposite the placement badge */}
        <div className="ipdi-eyebrow-badge">
          <IconBadge />
          {program.eyebrow}
        </div>
        {/* corner badge — text only, no checkmark icon */}
        {/* <div className="ipdi-corner-badge">{program.badge}</div> */}

        {/* title + meta row now overlaid at the bottom of the image */}
        <div className="ipdi-image-heading">
          <h2 className="ipdi-title">{program.title}</h2>
          <div className="ipdi-meta-row">
            <span className="ipdi-meta-chip">
              <IconClock />
              {program.meta[0]}
            </span>
            <span className="ipdi-meta-dot" />
            <span className="ipdi-meta-chip">
              <IconVideo />
              {program.meta[1]}
            </span>
          </div>
        </div>
      </div>

      <div className="ipdi-card-body-wrap">
        <div className="ipdi-card-body">
          <p className="ipdi-value-prop">{program.valueProp}</p>

          <div className="ipdi-highlight-grid">
            {program.highlights.map((h, i) => {
              const Icon = highlightIcons[i % highlightIcons.length];
              return (
                <div className="ipdi-highlight-item" key={h}>
                  <Icon />
                  <span>{h}</span>
                </div>
              );
            })}
          </div>

          <div className="ipdi-audience-label">Built For</div>
          <div className="ipdi-audience-row">
            {program.audience.map((a) => (
              <span className="ipdi-audience-chip" key={a}>
                {a}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="ipdi-card-footer">
        <a className="ipdi-cta-primary" href={program.link}>
          {program.ctaLabel}
          <IconArrow />
        </a>
      </div>
    </div>
  );
}

/* ---------------- Row wrapper + heading + styles ---------------- */

export default function ProgramCardsRow() {
  return (
    <div className="ipdi-programs-wrap" id="programs">
      <style>{css}</style>

      <div className="ipdi-section-heading">
        <div className="ipdi-heading-eyebrow">
          <span className="ipdi-heading-dash"></span>
          What We Offer
        </div>
        <h2 className="ipdi-heading-title">
          Programs That <em>Meet Industry Demands</em>
        </h2>
      </div>

      <div className="ipdi-grid-context">
        {programs.map((p) => (
          <ProgramCard program={p} key={p.title} />
        ))}
      </div>
    </div>
  );
}

const css = `
.ipdi-programs-wrap{
  --void:#FFFFFF;
  --panel:#1A1613;
  --text:#F5F1E8;
  --muted:#A39C8E;
  --muted-dim:#6E675C;
  --accent:#C24A1E;
  --accent-light:#DE7042;
  --accent-dim:rgba(194,74,30,0.14);
  --hair:rgba(245,241,232,0.08);
  --hair-strong:rgba(245,241,232,0.15);
  background:var(--void);
  padding:48px 20px;
  font-family:'Inter',sans-serif;
}
.ipdi-programs-wrap *{box-sizing:border-box;}

.ipdi-programs-wrap{

           padding: 60px 0 100px 0;

}

/* ---- Heading ---- */
.ipdi-section-heading{
  max-width:100%;
  margin:0 0 32px;
  padding:0 2.5rem;
  text-align:start;
}

.ipdi-heading-eyebrow{
  display:inline-flex;align-items:center;gap:8px;
  font-family:'JetBrains Mono',monospace;
  font-size:11px;font-weight:700;letter-spacing:0.14em;text-transform:uppercase;
  color:var(--accent);
  margin-bottom:12px;
  text-align:start;
}
.ipdi-heading-dash{
  width:22px;height:2px;background:var(--accent);display:inline-block;
}
.ipdi-heading-title{
  font-family:var(--serif);
  font-weight:400;
  font-size:clamp(28px, 4vw, 52px);
  text-align:start;
  line-height:1.2;
  color:var(--charcoal);
  margin:0;
  letter-spacing:-0.01em;
  margin-bottom:60px;
}
.ipdi-heading-title em{
  color:var(--accent);
  font-style:italic;
  font-weight:400;
   color: #c84b20;
}

.ipdi-grid-context{
  display:flex;
  gap:20px;
  align-items:stretch;
  flex-wrap:wrap;
  justify-content:flex-start;
  max-width:100%;
  margin:0;
  padding:0 2.5rem;
}

.ipdi-card{
  position:relative;
  width:382px;
  background:var(--panel);
  border:1px solid var(--hair-strong);
  border-radius:4px;
  overflow:hidden;
  color:var(--text);
  box-shadow:0 30px 60px -24px rgba(0,0,0,0.7);
  flex-shrink:0;
  display:flex;
  flex-direction:column;
  transition:background 0.6s ease, border-color 0.6s ease, box-shadow 0.6s ease;
}

/* ---- Glass shine sweep (no scale/zoom involved) ---- */
.ipdi-card::after{
  content:"";
  position:absolute;
  top:0;
  left:-75%;
  width:35%;
  height:100%;
  background:linear-gradient(
    100deg,
    rgba(255,255,255,0) 0%,
    rgba(255,255,255,0.12) 45%,
    rgba(255,255,255,0.16) 50%,
    rgba(255,255,255,0.12) 55%,
    rgba(255,255,255,0) 100%
  );
  transform:skewX(-15deg);
  pointer-events:none;
  z-index:6;
  opacity:0;
  transition:left 1.8s cubic-bezier(.4,0,.2,1), opacity 0.8s ease-in-out;
}
.ipdi-card:hover::after{
  left:135%;
  opacity:1;
}

.ipdi-card:hover{
  // background: #a92c05;
   background:var(--panel);
  border-color: #a92c05;
  box-shadow:0 30px 60px -24px rgba(200,75,32,0.45);
}

/* ---- Desktop only: cards stretch to fill the row so left/right
   padding stays equal and matches the header alignment ---- */
@media (min-width:769px){
  .ipdi-grid-context{
    flex-wrap:nowrap;
  }
  .ipdi-card{
    width:auto;
    flex:1 1 0;
    min-width:0;
  }
}

.ipdi-card-image{
  position:relative;
  height:200px;
  overflow:hidden;
  background:radial-gradient(130% 150% at 15% 0%, #241E18 0%, #0D0B09 72%);
  transition:background 0.6s ease;
}
.ipdi-card-image img{
  position:absolute;
  inset:0;
  width:100%;
  height:100%;
  object-fit:cover;
  object-position:center;
  transition:opacity 0.5s ease;
}
.ipdi-card-image::after{
  content:"";
  position:absolute;
  inset:0;
  background:linear-gradient(180deg, transparent 30%, rgba(13,11,9,0.94) 100%);
  transition:opacity 0.5s ease;
}

/* ---- Hover: fade the image + its dark overlay out, and let the
   card-image background take on the same accent color as the card
   so the whole card reads as one solid color block ---- */
.ipdi-card:hover .ipdi-card-image{
  // background:#a92c05;
   background:var(--panel);
}
.ipdi-card:hover .ipdi-card-image img{
  opacity:0;
}
.ipdi-card:hover .ipdi-card-image::after{
  opacity:0;
}

.ipdi-card-body-wrap{position:relative;}
.ipdi-card-body-wrap::before{
  content:"";
  position:absolute;
  top:0; left:0; right:0; bottom:0;
  background-image:
    linear-gradient(var(--hair) 1px, transparent 1px),
    linear-gradient(90deg, var(--hair) 1px, transparent 1px);
  background-size:22px 22px;
  opacity:0.35;
  pointer-events:none;
  -webkit-mask-image:linear-gradient(180deg, black, transparent 45%);
          mask-image:linear-gradient(180deg, black, transparent 45%);
  transition:background-image 0.35s ease;
}
.ipdi-card:hover .ipdi-card-body-wrap::before{
  background-image:
    linear-gradient(rgba(245,241,232,0.12) 1px, transparent 1px),
    linear-gradient(90deg, rgba(245,241,232,0.12) 1px, transparent 1px);
}

.ipdi-corner-badge{
  position:absolute;
  top:12px; right:12px;
  font-size:9px;font-weight:800;letter-spacing:0.03em;text-transform:uppercase;
  color:#FDFBF6;
  background:rgba(13,11,9,0.92);
  border:1px solid rgba(245,241,232,0.25);
  padding:5px 10px;border-radius:100px;
  white-space:nowrap;
  z-index:2;
}

.ipdi-eyebrow-badge{
  position:absolute;
  top:12px; left:12px;
  display:inline-flex;align-items:center;gap:5px;
  font-family:'JetBrains Mono',monospace;
  font-size:10px;font-weight:700;letter-spacing:0.08em;text-transform:uppercase;
  // color:#FDFBF6;
  // background:rgba(194,74,30,0.92);
  color: white;
  background:black;
  border:1px solid rgba(245,241,232,0.25);
  padding:5px 10px 5px 8px;border-radius:100px;
  white-space:nowrap;
  z-index:2;
}
.ipdi-eyebrow-badge svg{width:11px;height:11px;}

/* title + meta row, overlaid at the bottom of the image */
.ipdi-image-heading{
  position:absolute;
  left:0; right:0; bottom:0;
  padding:0 20px 1px;
  z-index:2;
}

.ipdi-card-body{position:relative;padding:16px 20px 12px;}

.ipdi-title{
  font-family:'Playfair Display',serif;
  font-weight:400;
  font-size:22px;
  line-height:1.18;
  margin:0 0 8px;
  letter-spacing:-0.005em;
  color:#FDFBF6;
}

.ipdi-meta-row{display:flex;flex-wrap:wrap;align-items:center;gap:8px;}
.ipdi-meta-chip{
  font-family:'JetBrains Mono',monospace;
  font-size:12px;font-weight:600;
  // color:rgba(245,241,232,0.75);
  color: #fff;
  display:flex;align-items:center;gap:5px;
}
.ipdi-meta-chip svg{width:12px;height:12px;opacity:0.85;color:var(--accent-light);}
.ipdi-meta-dot{width:3px;height:3px;border-radius:50%;background:rgba(245,241,232,0.4);flex-shrink:0;}

.ipdi-value-prop{
  font-size:13px;
  line-height:1.55;
  // color:var(--text);
  color: #A39C8E;
  opacity:0.92;
  margin:0 0 4px;
  font-weight:200;
  min-height:50px;
  transition:color 0.3s ease;
}
.ipdi-card:hover .ipdi-value-prop{
  color:var(--text);
  opacity:1;
}

.ipdi-highlight-grid{
  display:flex;
  flex-direction:column;
  gap:6px;
  margin-bottom:15px;
}
  
.ipdi-highlight-item{
  position:relative;
  display:flex;
  align-items:center;
  gap:9px;
  background:rgba(245,241,232,0.03);
  border:1px solid var(--hair);
  border-radius:7px;
  padding:3px 10px;
  transition:background 0.2s ease, border-color 0.2s ease;
}
.ipdi-highlight-item:hover{background:rgba(194,74,30,0.07);}
.ipdi-highlight-item svg{width:14px;height:14px;color:var(--accent-light);flex-shrink:0;}
.ipdi-highlight-item span{
  font-family:'JetBrains Mono',monospace;font-size:12px;font-weight:400;color:var(--muted);
  line-height:1.3;white-space:nowrap;letter-spacing:0.01em;overflow:hidden;text-overflow:ellipsis;
  transition:color 0.3s ease;
}
.ipdi-card:hover .ipdi-highlight-item{
  background:rgba(245,241,232,0.05);
  border-color:rgba(245,241,232,0.2);
}
.ipdi-card:hover .ipdi-highlight-item span{
  color:var(--text);
}

.ipdi-audience-label{
  font-family:'JetBrains Mono',monospace;
  font-size:11px;font-weight:600;letter-spacing:0.1em;text-transform:uppercase;
  color:var(--muted-dim);
  margin-bottom:8px;
  transition:color 0.3s ease;
}
.ipdi-card:hover .ipdi-audience-label{
  color:var(--muted);
}
.ipdi-audience-row{display:flex;flex-wrap:nowrap;gap:6px;}
.ipdi-audience-chip{
  flex:1 1 0;min-width:0;text-align:center;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;
  font-size:10px;font-weight:500;
  // color:var(--text);
  color: #A39C8E;
  border:1px solid var(--hair-strong);
  background:rgba(245,241,232,0.04);
  padding:4px 8px;border-radius:4px;
  transition:color 0.3s ease, border-color 0.3s ease, background 0.3s ease;
}
.ipdi-card:hover .ipdi-audience-chip{
  color:var(--text);
  border-color:rgba(245,241,232,0.3);
  background:rgba(245,241,232,0.06);
}

.ipdi-card-footer{
  margin-top:auto;
  padding:10px 20px 10px;
  background:linear-gradient(180deg, transparent, rgba(0,0,0,0.15));
  border-top:1px solid var(--hair);
  transition:border-color 0.3s ease;
}
.ipdi-card:hover .ipdi-card-footer{
  border-top-color:rgba(245,241,232,0.15);
}
.ipdi-cta-primary{
  display:flex;align-items:center;justify-content:center;gap:8px;
  width:100%;
  background:var(--accent);
  color:#FDFBF6;
  text-decoration:none;
  font-family:'Inter',sans-serif;
  font-weight:700;
  font-size:13px;
  padding:6px;
  border:1px solid transparent;
  border-radius:7px;
  cursor:pointer;
  position:relative;
  overflow:hidden;
  transition:box-shadow 0.15s ease, background 0.15s ease, border-color 0.2s ease;
}
.ipdi-cta-primary:hover{background:var(--accent-light);box-shadow:0 12px 24px -8px rgba(194,74,30,0.5);}
.ipdi-cta-primary svg{width:14px;height:14px;}
.ipdi-card:hover .ipdi-cta-primary{
  background:#FFFFFF;
  color:#c84b20;
  border-color:transparent;
}
.ipdi-card:hover .ipdi-cta-primary:hover{
  background:#F5F1E8;
  color:#c84b20;
  box-shadow:0 12px 24px -8px rgba(0,0,0,0.35);
}

@media (max-width:768px){

  .ipdi-section-heading{
    padding:0 16px;
    margin-bottom:24px;
  }

  .ipdi-grid-context{
    padding:0 16px;
    justify-content:center;
    gap:18px;
  }

  .ipdi-card{
    width:100%;
    max-width:100%;
  }

}
`;
